/**
 * @lodex-pro/shared/tank-level-format — Phase 13 / task 13.8 (D-23c): the one way a tank reading
 * is shown, in the operator portal, dispatch and the Operator API's consumers.
 *
 * A reading is shown AS STORED. A level given in inches gets barrels only from the tank's own bbl
 * per inch, and only in the database (0245's tank_readings_fill_bbl and its back-fill). Until
 * then it is shown raw: "8 ft 4 in, size unknown". Nothing here converts inches to barrels, and
 * by design no function here takes a size: never show an estimated BBL from a level (Corbin,
 * 2026-10-27: "flag it and show the raw values").
 */

/** A tank's latest reading as stored: raw when unconverted (volumeBbl null, needsSize true). */
export interface TankLatestReading {
  /** The level in inches when it was given in inches, else null. */
  levelIn: number | null;
  /** Barrels, or null while the level is unconverted. */
  volumeBbl: number | null;
  /** True when the level is in inches and the tank has no bbl per inch of its own yet. */
  needsSize: boolean;
  /** ISO 8601. */
  readAt: string;
}

/** One decimal at most, with no trailing ".0": 4 → "4", 0.5 → "0.5". */
function tenths(n: number): string {
  const r = Math.round(n * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
}

/**
 * A level in inches as a person reads a gauge: "8 ft 4 in" over 12 in, "7.5 in" at 12 in or
 * below. Rounded to 0.1 in first, so 23.96 in is "2 ft 0 in", never "1 ft 12 in".
 */
export function formatRawLevel(inches: number): string {
  const total = Math.round(inches * 10) / 10;
  if (total <= 12) return `${tenths(total)} in`;
  const ft = Math.floor(total / 12);
  const rest = Math.round((total - ft * 12) * 10) / 10;
  return `${String(ft)} ft ${tenths(rest)} in`;
}

const bbl = (v: number): string => `${v.toLocaleString('en-US', { maximumFractionDigits: 1 })} BBL`;

/**
 * A reading for the screen: "257 BBL", "167 BBL (8 ft 4 in)", or, unconverted,
 * "8 ft 4 in, size unknown". "—" when it carries neither.
 */
export function formatTankReading(
  r: Pick<TankLatestReading, 'levelIn' | 'volumeBbl' | 'needsSize'>,
): string {
  const volume = r.volumeBbl !== null && Number.isFinite(r.volumeBbl) ? r.volumeBbl : null;
  const level = r.levelIn !== null && Number.isFinite(r.levelIn) ? r.levelIn : null;
  if (volume !== null && level !== null) return `${bbl(volume)} (${formatRawLevel(level)})`;
  if (volume !== null) return bbl(volume);
  if (level !== null) return `${formatRawLevel(level)}, size unknown`;
  return '—';
}
